import ability from '@/libs/acl/ability'
import axios from '@axios'
import { SLOT_TYPES, SOURCES } from '@/config/layoutConfig'

class AlarmViewService {
  constructor(store) {
    this.store = store
  }

  getNormalizedAlarmViewLink(viewLink) {
    if (!viewLink || typeof viewLink !== 'string') {
      return null
    }

    if (/^https?:\/\//i.test(viewLink)) {
      try {
        const parsed = new URL(viewLink, window.location.origin)
        return `${parsed.pathname}${parsed.search}${parsed.hash}`
      } catch (_) {
        return null
      }
    }

    return viewLink
  }

  getAlarmTabName(alarm) {
    const unitDescription = alarm?.unit?.description || ''
    const alarmType = alarm?.alarm_type || ''
    if (unitDescription && alarmType) {
      return `Alarma ${unitDescription} | ${alarmType}`
    }
    if (unitDescription) {
      return `Alarma ${unitDescription}`
    }
    if (alarmType) {
      return `Alarma ${alarmType}`
    }
    return 'Alarma'
  }

  async setAlarmView(payload) {
    try {
      const alarm = payload.alarm
      const user = this.store.getters['user/getUser']
      let view = payload.view || null
      let links = []

      if (!view) {
        try {
          const normalizedViewLink = this.getNormalizedAlarmViewLink(alarm?.view_link)
          if (!normalizedViewLink) {
            return this.setDefaultAlarmView(alarm)
          }
          const { data } = await axios.get(normalizedViewLink)
          const raw = data.data
          view = raw && typeof raw === 'object' ? (raw.view ?? raw) : null
          const { data: linksData } = await axios.get(`v1/page`, { params: { id_user: user.id } })
          links = linksData.data || []
        } catch (error) {
          return this.setDefaultAlarmView(alarm)
        }
      } else {
        const { data: linksData } = await axios.get(`v1/page`, { params: { id_user: user.id } })
        links = linksData.data || []
      }

      if (!view || typeof view !== 'object') {
        return this.setDefaultAlarmView(alarm)
      }

      const viewTabs = this.getNormalizedViewTabs(view)
      if (viewTabs.length > 1) {
        await this.setAlarmViewWithMultipleTabs(viewTabs, alarm, links)
        return
      }

      const slots = viewTabs[0]?.layout_config || view.layout_config || []
      await this.processSlotsConfiguration(slots, alarm, links)

      const tabConfig = this.createTabConfiguration(view, alarm)
      const tab = await this.store.dispatch("grid/addNewTab", tabConfig)

      if (tab.status) {
        this.store.dispatch("grid/setActiveTab", tab.id)
        this.store.commit("grid/forceLayoutSlots", slots)
      }
    } catch (error) {
      console.error('setAlarmViewError:', error)
    }
  }

  getNormalizedViewTabs(view) {
    if (!view || typeof view !== 'object') {
      return [{
        name: 'Tap 1',
        id_layout: null,
        layout_config: [],
      }]
    }
    const tabs = view.tabs ?? view.data?.tabs ?? []
    if (Array.isArray(tabs) && tabs.length > 0) {
      return tabs.map((t) => ({
        name: t.name || 'Tap',
        id_layout: t.id_layout,
        layout_config: Array.isArray(t.layout_config) ? t.layout_config : [],
      }))
    }
    return [{
      name: `Alarm ${view.name || ''}`.trim() || 'Tap 1',
      id_layout: view.id_layout,
      layout_config: Array.isArray(view.layout_config) ? view.layout_config : [],
    }]
  }

  getAlarmTabBaseName(alarm) {
    const base = `Alarm ${alarm.unit?.description ?? ''} | ${alarm.alarm_type ?? ''}`.trim()
    return base || 'Alarma'
  }

  async setAlarmViewWithMultipleTabs(viewTabs, alarm, links) {
    const normalizedTabs = viewTabs.map((vt) => ({
      ...vt,
      layout_config: Array.isArray(vt.layout_config) ? vt.layout_config : [],
    }))
    await Promise.all(
      normalizedTabs.map((vt) => this.processSlotsConfiguration(vt.layout_config, alarm, links))
    )

    const baseName = this.getAlarmTabBaseName(alarm)
    const createdTabIds = []
    for (let i = 0; i < normalizedTabs.length; i++) {
      const viewTab = normalizedTabs[i]
      const tabName = normalizedTabs.length === 1
        ? baseName
        : `${baseName} - ${i + 1}`
      const tabConfig = {
        tabName,
        layout: viewTab.id_layout || 'default',
        tabColor: alarm.alarm_level?.color,
        tabContext: { isAlarm: true, alarmId: alarm.id },
      }
      const result = await this.store.dispatch("grid/addNewTab", tabConfig)
      if (result.status && result.id) createdTabIds.push(result.id)
    }

    for (let i = 0; i < createdTabIds.length; i++) {
      this.store.dispatch("grid/setActiveTab", createdTabIds[i])
      const slots = Array.isArray(normalizedTabs[i].layout_config) ? normalizedTabs[i].layout_config : []
      this.store.commit("grid/forceLayoutSlots", slots)
    }

    if (createdTabIds.length > 0) {
      this.store.dispatch("grid/setActiveTab", createdTabIds[0])
    }
  }

  async setDefaultAlarmView(alarm) {
    const slots = this.createDefaultAlarmSlots(alarm)
    const tabName = this.getAlarmTabName(alarm)
    const tab = await this.store.dispatch("grid/addNewTab", {
      tabName,
      layout: 'default',
      slots,
      tabContext: {
        isAlarm: true,
        alarmId: alarm.id,
      }
    })

    if (tab.status) {
      await this.store.dispatch("grid/setActiveTab", tab.id)
      this.store.commit("grid/forceLayoutSlots", slots)
    }
  }

  createDefaultAlarmSlots(alarm) {
    const slots = []
    const defaultConfig = require('@/config/defaultLayout').default.grid
    const alarmCameras = Array.isArray(alarm?.cameras) ? alarm.cameras : []
    const alarmUnit = alarm?.unit || {}
    const camera = alarm?.detection_event_data?.camera_data?.camera || alarmCameras[0] || null
    const alarmType = alarm?.type

    if (camera && ability.can('live_video', 'Video')) {
      const gridItem = defaultConfig.find(item => item.pos === 1) || defaultConfig[0]
      slots.push({
        name: camera.name,
        pos: 1,
        data: {
          camera: camera,
          ts: alarm?.created,
          region: alarmUnit?.region,
          host: alarm?.host
        },
        type: SLOT_TYPES.CAM_LIVE,
        id: gridItem.id
      })
    }

    const infoGridItem = defaultConfig.find(item => item.pos === 2) || defaultConfig[1]
    slots.push({
      name: 'Alarm info',
      pos: 2,
      data: {
        ...alarm,
        in_app_source: SOURCES.ALARM_VIEW
      },
      type: SLOT_TYPES.INFO,
      id: infoGridItem.id
    })

    const mapGridItem = defaultConfig.find(item => item.pos === 3) || defaultConfig[2]
    if (alarmType === 5) {
      slots.push({
        name: 'Alarm tracking map',
        pos: 3,
        data: { alarm, unit_id: alarmUnit?.id },
        type: SLOT_TYPES.FR_TRACKING_MAP,
        id: mapGridItem.id
      })
    } else if (alarmType === 6) {
      slots.push({
        name: 'Alarm tracking map',
        pos: 3,
        data: { alarm, unit_id: alarmUnit?.id },
        type: SLOT_TYPES.LPR_TRACKING_MAP,
        id: mapGridItem.id
      })
    } else {
      slots.push({
        name: 'Alarm map',
        pos: 3,
        data: {
          data: alarm,
          unit_id: alarmUnit?.id,
          in_app_source: SOURCES.ALARM_VIEW
        },
        type: SLOT_TYPES.UNITS_MAP,
        id: mapGridItem.id
      })
    }

    const actionPlanGridItem = defaultConfig.find(item => item.pos === 4) || defaultConfig[3]
    slots.push({
      name: 'Action plan',
      pos: 4,
      data: {
        ...alarm,
        in_app_source: SOURCES.ALARM_VIEW
      },
      type: SLOT_TYPES.ACTION_PLAN,
      id: actionPlanGridItem.id
    })

    return slots
  }

  async processLayoutConfiguration(view, alarm, links) {
    const slots = view.layout_config || []
    await this.processSlotsConfiguration(slots, alarm, links)
  }

  async processSlotsConfiguration(slots, alarm, links) {
    const cameras = Array.isArray(alarm?.cameras) ? alarm.cameras : []
    await Promise.all(slots.map((slot) => this.configureSlotData(slot, alarm, cameras, links)))
  }

  async resolveUnitPanelIds(alarm) {
    const unitId = alarm?.unit?.id
    if (!unitId) return []

    try {
      const response = await this.store.dispatch('dmp_panels/getAll', { units: [unitId], max: 100 })
      const panels = response?.data ?? []
      return panels.map((panel) => panel.id)
    } catch (error) {
      console.error('resolveUnitPanelIds error:', error)
      return []
    }
  }
  async resolveUnitDvrIds(alarm) {
    const unitId = alarm?.unit?.id
    if (!unitId) return []

    try {
      const response = await this.store.dispatch('hanwha_dvr/getAll', { id_unit: [unitId], max: 100 })
      const dvrs = response?.data ?? []
      return dvrs.map((dvr) => dvr.id)
    } catch (error) {
      console.error('resolveUnitDvrIds error:', error)
      return []
    }
  }

  formatDateTime(date) {
    const pad = (n) => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} `
      + `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  }
  resolveDateRange(days) {
    const end = new Date()
    const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000)
    return {
      startDatetime: this.formatDateTime(start),
      endDatetime: this.formatDateTime(end),
    }
  }

  async configureSlotData(slot, alarm, cameras, links) {
    switch (slot.type) {
      case SLOT_TYPES.INFO:
        slot.data = { ...alarm, in_app_source: SOURCES.ALARM_VIEW }
        break
      case SLOT_TYPES.MAP:
        slot.data = { data: alarm }
        break
      case SLOT_TYPES.GPS:
        slot.data = { data: alarm, unit_id: alarm?.unit?.id }
        break
      case SLOT_TYPES.UNITS_MAP:
        slot.data = { unit_id: alarm?.unit?.id }
        break
      case SLOT_TYPES.PLANE:
        slot.data = {
          planes_url: alarm?.unit_planes ?? alarm?.unit?.unit_planes ?? null,
          unit_id: alarm?.unit?.id ?? null,
        }
        break
      case SLOT_TYPES.DETECTION_EVENT:
        slot.data = { in_app_source: SOURCES.ALARM_VIEW }
        break
      case SLOT_TYPES.TAG:
        this.processTagSlot(slot, alarm, cameras)
        break
      case SLOT_TYPES.ACTION_PLAN:
        slot.data = alarm
        break
      case SLOT_TYPES.FRAME:
        const link = links.find(link => link.id === slot.data.link_id)
        if (link) {
          link.load = true
          slot.data = link
        }
        break
      case SLOT_TYPES.DRAGGABLE_MENU:
        if (slot.data.source === 'dmp_logs') {
          if (slot.data.filters?.autoDetectPanel) {
            slot.data.filters.panelFilter = await this.resolveUnitPanelIds(alarm)
          }
          if (slot.data.filters?.dateRangeFilter) {
            Object.assign(slot.data.filters, this.resolveDateRange(slot.data.filters.dateRangeFilter))
          }
          slot.data = {
            filters: slot.data,
            type: 'dmp_logs',
          }
        }
        if (slot.data.source === 'omnicloud_fr') {
          slot.data = {
            filters: slot.data,
            type: 'omnicloud_face_recognition',
          }
        }
        if (slot.data.source === 'alarm') {
          // dateRangeFilter (30/60 days) overrides the fixed startDate/endDate
          // with a window computed from "now", each time the alarm view is opened.
          if (slot.data.filters?.dateRangeFilter) {
            const range = this.resolveDateRange(slot.data.filters.dateRangeFilter)
            slot.data.filters.startDate = range.startDatetime
            slot.data.filters.endDate = range.endDatetime
          }
          slot.data = {
            filters: slot.data,
            type: 'alarm',
          }
        }
        if (slot.data.source === 'panel') {
          slot.data = {
            alarm: alarm,
            type: 'panel',
          }
        }
        if (slot.data.source === 'hanwha_logs') {
          if (slot.data.filters?.autoDetectDvr) {
            slot.data.filters.dvrFilter = await this.resolveUnitDvrIds(alarm)
          }
          if (slot.data.filters?.dateRangeFilter) {
            Object.assign(slot.data.filters, this.resolveDateRange(slot.data.filters.dateRangeFilter))
          }
          slot.data = {
            filters: slot.data,
            type: 'hanwha_logs',
          }
        }
        break
    }
  }

  processTagSlot(slot, alarm, cameras) {
    const chunks = slot.data.tag.split(':')
    const tag = chunks[0]
    const isCAM_LIVE = chunks.length === 1

    const matchingCameras = cameras
      .filter(cam => cam)
      .filter(cam => cam.tag === tag)

    if (matchingCameras.length === 0) {
      slot.type = null
      slot.data = null
      return
    }

    if (matchingCameras.length > 1) {
      slot.type = SLOT_TYPES.MULTI_CAMERA
      slot.data = {
        cameras: matchingCameras,
        tag,
        ts: isCAM_LIVE ? null : alarm.created,
        host: alarm.host,
        isLive: isCAM_LIVE,
        in_app_source: SOURCES.ALARM_VIEW
      }
      return
    }

    const camera = matchingCameras[0]
    if (isCAM_LIVE && ability.can('live_video', 'Video')) {
      slot.data = {
        camera,
        ts: null,
        host: alarm.host,
        in_app_source: SOURCES.ALARM_VIEW
      }
      slot.type = SLOT_TYPES.CAM_LIVE
    } else if (ability.can('record_video', 'Video')) {
      slot.data = {
        camera,
        ts: alarm.created,
        host: alarm.host,
        in_app_source: SOURCES.ALARM_VIEW
      }
      slot.type = SLOT_TYPES.CAM_PA
    } else {
      slot.type = null
      slot.data = null
    }
  }

  createTabConfiguration(view, alarm) {
    return {
      tabName: this.getAlarmTabName(alarm),
      layout: view.id_layout,
      tabColor: alarm.alarm_level?.color,
      tabContext: {
        isAlarm: true,
        alarmId: alarm.id,
      }
    }
  }
}

export default AlarmViewService
